import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Params } from '@angular/router';
import { StorageService } from '../../services/storage/storage.service';
import { AuthService, LoginRequest } from '../../services/auth/auth.service';
import { SharedService } from '../../services/shared/shared.service';
import { LoginData } from '../../app.component';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { RoutingService } from '../../services/routing/routing.service';

@Injectable()
export class LoginGuard {
  constructor(
    private _storageService: StorageService,
    private _authService: AuthService,
    private _sharedService: SharedService,
    private _routingService: RoutingService,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> | boolean {
    if (this._authService.IsAuthenticated()) {
      return true;
    }

    return new Observable<boolean>((obs) => {
      if (
        this._sharedService.isEmptyObject(route.queryParams) &&
        !this._authService.getUserDataFromSession()
      ) {
        window.location.href = this._routingService.baseUri;
      }

      this._setupLoginData(route.queryParams)
        .pipe(take(1))
        .subscribe((loginData) => {
          this._login(loginData).subscribe(
            (userData) => {
              if (userData) {
                //TODO This isn't a good indicator for redirectOnAutoLogin
                if (
                  SharedService.isNotEmpty(loginData.username) &&
                  SharedService.isNotEmpty(loginData.password)
                ) {
                  this._routingService.redirectOnAutoLogin(
                    this._removeLoginQueryParams(route.queryParams),
                  );
                }

                obs.next(true);
              } else {
                obs.next(false);
              }
            },
            () => {
              window.location.href = this._routingService.baseUri;
            },
          );
        });
    });
  }

  private _setupLoginData(params?: Params): Observable<LoginData> {
    let cid: string,
      lid: string,
      sessionId: string,
      username: string,
      password: string,
      uid: string,
      body: string;

    if (params) {
      cid = params['cid'];
      lid = params['lid'];
      sessionId = params['sessionid'] ?? params['sessionId'] ?? params['SessionId'];
      username = params['uname'];
      password = params['pass'];
      uid = params['uid'];
      body = params['body'];
    }

    if (SharedService.isEmpty(cid)) {
      cid = this._storageService.getCid();
    }
    if (SharedService.isEmpty(lid)) {
      lid = this._storageService.getLid();
    }

    // 31273 MassVC: Login OpenURL for Login Widgets needs to be created/documented
    if (
      SharedService.isNotEmpty(cid) &&
      SharedService.isNotEmpty(uid) &&
      uid.length > 6 &&
      SharedService.isNotEmpty(body) &&
      body == 'true'
    ) {
      return this._authService.getLoginRequest(uid, cid).pipe(
        switchMap((loginData: LoginRequest) => {
          return of({
            cid: loginData?.cid,
            lid: loginData?.lid,
            username: loginData?.userName,
            password: loginData?.password,
            sessionId: sessionId,
          });
        }),
      );
    }

    return of({ cid: cid, lid: lid, username: username, password: password, sessionId: sessionId });
  }

  private _login(loginData: LoginData) {
    if (SharedService.isEmpty(loginData.cid)) {
      throw new Error('No cid provided, can not login');
    }

    const userData = this._authService.getUserDataFromSession();

    if (loginData.sessionId) {
      return this._authService
        .login(this._createSessionLoginRequest(loginData.cid, loginData.sessionId))
        .pipe(
          catchError(() => {
            window.location.href = this._routingService.baseUri;
            return of();
          }),
        );
    } else if (
      userData &&
      this._storageService.getCid() == loginData.cid.toLowerCase() &&
      this._storageService.getLid() == loginData.lid.toLowerCase() &&
      SharedService.isEmpty(loginData.username) &&
      SharedService.isEmpty(loginData.password)
    ) {
      return this._authService
        .login(this._createSessionLoginRequest(userData.customerId, userData.sessionId))
        .pipe(
          catchError(() => {
            window.location.href = this._routingService.baseUri;
            return of();
          }),
        );
    } else {
      return this._authService.login(
        this._createLoginRequest(
          loginData.cid,
          loginData.lid,
          loginData.username,
          loginData.password,
        ),
      );
    }
  }

  private _createSessionLoginRequest(cid: string, sessionId: string): LoginRequest {
    return {
      cid: cid,
      sessionId: sessionId,
    } as LoginRequest;
  }

  private _createLoginRequest(
    cid: string,
    lid: string,
    username: string,
    password: string,
  ): LoginRequest {
    return {
      cid: cid,
      lid: lid,
      userName: username ? username : 'guest',
      password: password ? password : 'guest',
    } as LoginRequest;
  }

  private _removeLoginQueryParams(queryParams: Params) {
    const params = { ...queryParams };
    delete params.cid;
    delete params.lid;
    delete params.uname;
    delete params.pass;

    return params;
  }
}
